import React, { useState, useEffect, useRef } from 'react';
import { useLiveTicks, tickLtp } from '../services/liveTicks';
import '../styles/GreekStopLossPanel.css';

// Trail milestones — gain % → what the backend's informational nudge suggests at each level.
// Purely descriptive here (pre-trade rules preview + progress bar labels) — the backend
// (ManualTradeTrackingService) owns the actual trailing-stop math and auto-close decision.
const TRAIL_MILESTONES = [
  { id: '30',  gainPct: 30,  label: '+30%', action: 'Move SL to breakeven' },
  { id: '50',  gainPct: 50,  label: '+50%', action: 'Trail SL to +20%' },
  { id: '100', gainPct: 100, label: '+100%',action: 'Exit half, trail rest' },
];

const GreekStopLossPanel = ({
  gammaData, symbol, expiryDate, onRecordSignal, onSquareOff, defaultLots, systemVerdict,
  manualPositions = [], tradeHistory = [],
}) => {

  // ── Inputs ────────────────────────────────────────────────────────────────
  const [side,           setSide]           = useState('CE');
  const [entryPremium,   setEntryPremium]   = useState('');
  const [strikePrice,    setStrikePrice]    = useState('');  // user's actual traded strike
  // Seeded from the user's own saved risk setting (Menu → Risk Settings) — still freely
  // editable per trade, this is just a sensible personal default instead of a hardcoded '1'.
  const [lots,           setLots]           = useState(defaultLots != null ? String(defaultLots) : '1');
  const [maxLossPct,     setMaxLossPct]     = useState('30');
  const [accountCapital, setAccountCapital] = useState('');

  // ── Trade tracking state ──────────────────────────────────────────────────
  // Trailing-SL/milestone/auto-close tracking now lives entirely on the backend
  // (ManualTradeTrackingService) — this panel only submits the trade and renders whatever
  // the backend reports via manualPositions (live SSE) / tradeHistory (once closed).
  const [tracking,       setTracking]       = useState(false);    // in tracking mode
  const [trade,          setTrade]          = useState(null);     // locked trade snapshot
  const [squaredOff,     setSquaredOff]     = useState(false);    // optimistic local close (manual click)
  const [exitLTP,        setExitLTP]        = useState(null);

  // Holds the trade object that was passed to onRecordSignal so square-off can pass the same
  // object (with the same id) to onSquareOff for a proper P&L patch, and so this panel can
  // match itself against manualPositions/tradeHistory by clientId.
  const recordedTradeRef   = useRef(null);

  // ── Backend-reported status for this open position (SSE, personalized per user) ────────
  const myClientId   = recordedTradeRef.current?.id ?? null;
  const backendPos   = (tracking && myClientId != null)
    ? manualPositions.find(p => Number(p.clientId) === myClientId) : null;
  // Backend already closed this trade (auto square-off, or a UI square-off from another tab)
  // before this tab's own optimistic squaredOff state was set — confirmed via the merged
  // tradeHistory record once it refreshes.
  const backendClosedRecord = (tracking && !squaredOff && myClientId != null)
    ? tradeHistory.find(t => t.id === myClientId && t.outcome !== 'OPEN') : null;
  const isClosed = squaredOff || !!backendClosedRecord;

  // Destructure with safe defaults so hooks below never see undefined
  const {
    spotPrice, atmStrike,
    atmCallDelta, atmPutDelta,
    atmCallTheta, atmPutTheta,
    atmVega, atmGamma,
    atmCallIV, atmPutIV,
    atmCallLTP, atmPutLTP,
    strikeData: strikeDataArr,
    lotSize = 75,
    thetaCallPct, thetaPutPct,
    ivRank, ivPercentile,
    timeZone, daysToExpiry,
  } = gammaData || {};

  // Live LTP for the tracked side. Prefers the backend's own reported currentLTP
  // (ManualTradeTrackingService, via SSE) — falls back to this tab's own chain lookup only
  // for the brief window between "Start Tracking" and the backend's first poll confirming it.
  const ticks = useLiveTicks(symbol);
  const liveLTP = (() => {
    if (!tracking || !trade) return null;
    const tickPx = tickLtp(ticks, expiryDate, trade.strikePrice, trade.side);   // 1 s Kite tick when fresh
    if (tickPx != null) return tickPx;
    if (backendPos?.currentLTP != null) return backendPos.currentLTP;
    const row = trade.strikePrice && strikeDataArr
      ? strikeDataArr.find(s => s.strikePrice === trade.strikePrice)
      : null;
    if (row) return trade.side === 'CE' ? (row.ceLTP ?? 0) : (row.peLTP ?? 0);
    return trade.side === 'CE' ? (atmCallLTP ?? 0) : (atmPutLTP ?? 0);
  })();

  // ── Calculator values (used in both modes) ────────────────────────────────
  const ep      = parseFloat(tracking ? trade?.ep      : entryPremium) || 0;
  const lotsNum = parseInt  (tracking ? trade?.lots    : lots)          || 1;
  const mlp     = parseFloat(tracking ? trade?.mlp     : maxLossPct)   || 30;
  const trSide  = tracking  ? trade?.side : side;
  const usedLotSize = trade?.lotSize ?? lotSize;

  // Resolve the relevant strike number in both modes:
  //   tracking  → locked trade.strikePrice
  //   calculator → strikePrice input field (if filled)
  // Use nearest strike in chain to handle values not exactly on a tick.
  const resolvedStrikeNum = tracking
    ? (trade?.strikePrice ?? null)
    : (parseFloat(strikePrice) || null);

  const strikeRow = (() => {
    if (!resolvedStrikeNum || !strikeDataArr?.length) return null;
    // exact match first
    const exact = strikeDataArr.find(s => s.strikePrice === resolvedStrikeNum);
    if (exact) return exact;
    // nearest match — handles minor rounding differences
    return strikeDataArr.reduce((best, s) =>
      Math.abs(s.strikePrice - resolvedStrikeNum) < Math.abs(best.strikePrice - resolvedStrikeNum) ? s : best
    );
  })();

  const delta = strikeRow
    ? Math.abs((trSide === 'CE' ? strikeRow.callDelta : strikeRow.putDelta) || 0)
    : Math.abs((trSide === 'CE' ? atmCallDelta : atmPutDelta) || 0);

  // Use BS theta directly — this is the actual per-day decay rate from the formula.
  // On expiry day this is annualised from the remaining hours, so daysThetaSL < 1d is expected.
  const theta = strikeRow
    ? Math.abs((trSide === 'CE' ? strikeRow.callTheta : strikeRow.putTheta) || 0)
    : Math.abs((trSide === 'CE' ? atmCallTheta : atmPutTheta) || 0);

  const vega = strikeRow
    ? Math.abs((trSide === 'CE' ? strikeRow.callVega : strikeRow.putVega) || 0)
    : Math.abs(atmVega || 0);

  // Breakeven uses the user's entered / locked strike, falling back to ATM.
  const breakevenBase = resolvedStrikeNum ?? atmStrike ?? spotPrice;

  const premiumSL   = ep > 0 ? ep * (1 - mlp / 100)  : null;
  const premiumLoss = ep > 0 ? ep * mlp / 100          : null;
  const spotMove    = ep > 0 && delta > 0 ? premiumLoss / delta : null;
  const spotSL      = spotMove != null
    ? trSide === 'CE' ? spotPrice - spotMove : spotPrice + spotMove : null;
  const daysThetaSL = ep > 0 && theta > 0 ? premiumLoss / theta : null;
  const thetaPerDay = theta * usedLotSize * lotsNum;
  const vegaRisk5pct = vega * 5 * usedLotSize * lotsNum;
  const totalRiskRs  = ep * mlp / 100 * usedLotSize * lotsNum;  // SL-based risk (used for budget / riskDriver)
  const maxLossRs    = ep * usedLotSize * lotsNum;              // true max loss = full premium paid
  const breakeven    = trSide === 'CE' ? breakevenBase + ep : breakevenBase - ep;

  // Current market value of the position (live)
  const tradeValue = liveLTP != null ? liveLTP * usedLotSize * lotsNum : null;

  let riskDriver = 'DELTA';
  if (ep > 0 && theta > 0 && daysThetaSL != null) {
    if (daysThetaSL <= 2)                       riskDriver = 'THETA';
    else if (vegaRisk5pct >= totalRiskRs * 0.4) riskDriver = 'VEGA';
  }

  const thetaPct          = trSide === 'CE' ? thetaCallPct : thetaPutPct;
  const thetaCarryDanger  = thetaPct != null && thetaPct > 2.5;
  const thetaCarryWarn    = thetaPct != null && thetaPct > 1.5 && thetaPct <= 2.5;
  const deltaExit         = 0.20;
  const deltaLow          = delta < deltaExit + 0.05;

  // DTE budget
  const capitalNum   = parseFloat(accountCapital) || 0;
  const dteBudgetPct = (() => {
    if (daysToExpiry == null) return 2;
    if (daysToExpiry === 0)  return 0.5;
    if (daysToExpiry <= 2)   return 1.0;
    if (daysToExpiry <= 7)   return 2.0;
    return 3.0;
  })();
  const dteBudgetRs    = capitalNum > 0 ? capitalNum * dteBudgetPct / 100 : null;
  const budgetBreached = dteBudgetRs != null && ep > 0 && totalRiskRs > dteBudgetRs;
  const riskPerLot     = ep > 0 ? ep * mlp / 100 * usedLotSize : null;
  const maxLotsByBudget = (dteBudgetRs != null && riskPerLot != null && riskPerLot > 0)
    ? Math.floor(dteBudgetRs / riskPerLot) : null;
  const budgetUtilPct  = (dteBudgetRs != null && dteBudgetRs > 0 && ep > 0)
    ? Math.min(200, totalRiskRs / dteBudgetRs * 100) : null;

  // ── Live P&L (tracking mode) ─────────────────────────────────────────────
  // activeSL comes from the backend's own ratcheted peak once it's reported one — that IS
  // the level the backend will actually auto-close at. Before the first backend poll
  // confirms tracking, falls back to the plain entry-based premium SL.
  const activeSL       = backendPos?.stopLevel ?? premiumSL;
  const pnlPts         = liveLTP != null && trade ? liveLTP - trade.ep : null;
  const pnlRs          = pnlPts  != null && trade ? Math.round(pnlPts * trade.lotSize * lotsNum) : null;
  const pnlPct         = pnlPts  != null && trade && trade.ep > 0 ? pnlPts / trade.ep * 100 : null;
  const slBreached     = liveLTP != null && activeSL != null && liveLTP <= activeSL;
  const milestonesHit  = backendPos?.milestonesHit ?? [];

  // Final exit values — either this tab's own optimistic manual-click close, or the
  // backend-confirmed record (covers auto square-off, or a square-off from another tab).
  const finalExitLTP   = exitLTP ?? backendClosedRecord?.exitLTP ?? null;
  const exitPnlPts     = isClosed && finalExitLTP != null && trade ? finalExitLTP - trade.ep
    : (backendClosedRecord?.pnlPts ?? null);
  const exitPnlRs      = exitPnlPts != null && trade
    ? Math.round(exitPnlPts * trade.lotSize * lotsNum) : null;

  // Strike recommendation for the auto-fill below — same rule as the backend's
  // selectEntryStrike and TradingSetupPanel.js's strikeOptions: ATM or one strike ITM, whichever
  // has |delta| in 0.50–0.60 (nearest 0.55), else ATM. This only pre-fills the field — the
  // trader can always type over it, and never overrides a value already entered.
  const recommendedStrikePrice = (() => {
    if (!atmStrike || !spotPrice || !strikeDataArr) return atmStrike;
    const call = side === 'CE';
    const key = call ? 'callDelta' : 'putDelta';
    const atm = strikeDataArr.find(s => s.strikePrice === atmStrike);
    const itm = strikeDataArr
      .filter(s => s[key] != null && (call ? s.strikePrice < atmStrike : s.strikePrice > atmStrike))
      .sort((a, b) => call ? b.strikePrice - a.strikePrice : a.strikePrice - b.strikePrice)[0];
    const mid = 0.55;
    const inBand = s => s?.[key] != null && Math.abs(s[key]) >= 0.50 && Math.abs(s[key]) <= 0.60;
    const picks = [atm, itm].filter(inBand).sort((a, b) => Math.abs(Math.abs(a[key]) - mid) - Math.abs(Math.abs(b[key]) - mid));
    return picks.length ? picks[0].strikePrice : atmStrike;
  })();

  // Auto-fill strike with the DTE-based recommendation when the field is empty
  useEffect(() => {
    if (!tracking && recommendedStrikePrice && !strikePrice) {
      setStrikePrice(String(recommendedStrikePrice));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedStrikePrice]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const [startError, setStartError] = useState(null); // rejection message from the backend guard
  const [starting,   setStarting]   = useState(false); // awaiting the backend's accept/reject

  const handleStartTracking = async () => {
    if (!entryPremium || parseFloat(entryPremium) <= 0) return;
    const usedStrike  = parseFloat(strikePrice) || atmStrike || spotPrice;
    const usedLots    = parseInt(lots) || 1;
    const usedMlp     = parseFloat(maxLossPct) || 30;
    const usedEp      = parseFloat(entryPremium);

    // Record this as a manual trade so it appears in Win Rate Tracker + EOD report.
    // isManual=true is the source-of-truth flag distinguishing this from system-generated
    // signals (used for Telegram messaging + excluded from adaptive-downgrade stats).
    // finalVerdict mirrors TradingSetupPanel's live TAKE TRADE / ON RISK verdict when it
    // matches this trade's direction, so the trade history badge shows TT/OR like any
    // other trade — falling back to 'MANUAL' when no matching system signal is active.
    const tradeDir = side === 'CE' ? 'call' : 'put';
    const mirroredVerdict = (systemVerdict?.dir === tradeDir &&
      (systemVerdict?.finalVerdict === 'TAKE TRADE' || systemVerdict?.finalVerdict === 'ON RISK'))
      ? systemVerdict.finalVerdict
      : 'MANUAL';
    const tradeEntry = {
      id:              Date.now(),
      timestamp:       new Date().toISOString(),
      symbol:          symbol || '—',
      expiryDate:      expiryDate ?? null,
      signal:          'MANUAL',
      verdict:         'MANUAL',
      finalVerdict:    mirroredVerdict,
      isManual:        true,
      dir:             tradeDir,
      callScore:       null,
      putScore:        null,
      confluenceScore: null,
      entryStrike:     usedStrike,
      entryLTP:        usedEp,
      lotSize:         lotSize,
      numLots:         usedLots,
      allocatedLotSize: lotSize * usedLots,
      slPct:           usedMlp / 100,
      outcome:         'OPEN',
    };

    // The backend can reject this — e.g. no live price feed for this symbol/strike today
    // (SENSEX/BANKEX with nobody logged into Kite) — so wait for it before entering
    // tracking mode, rather than optimistically showing a position the backend never
    // actually tracks.
    setStartError(null);
    setStarting(true);
    try {
      await onRecordSignal?.(tradeEntry);
    } catch (err) {
      setStarting(false);
      setStartError(err?.message || 'Could not start tracking — please try again.');
      return;
    }
    setStarting(false);

    recordedTradeRef.current = tradeEntry;
    setTrade({
      side,
      ep:          usedEp,
      strikePrice: usedStrike,
      lots:        usedLots,
      mlp:         usedMlp,
      lotSize,
      entrySpot:   spotPrice,
      entryTime:   new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }),
    });
    setSquaredOff(false);
    setExitLTP(null);
    setTracking(true);
  };

  const handleSquareOff = () => {
    // One accidental click here instantly closes a live position at whatever the market
    // happens to be trading at — no undo. Confirm before firing, same as TradeHistoryPanel's
    // own Square Off button (both act on this same trade once it's recorded).
    const sideLabel = trade?.side === 'CE' ? 'CALL' : 'PUT';
    const confirmMsg = `Square off ${sideLabel} ${trade?.strikePrice ?? ''} now at `
      + `${liveLTP != null ? `₹${liveLTP.toFixed(2)}` : 'unknown LTP'}?`;
    if (!window.confirm(confirmMsg)) return;

    setExitLTP(liveLTP);
    setSquaredOff(true);

    // Push the close to Win Rate Tracker + EOD report via the same handler as auto-trades.
    if (recordedTradeRef.current && onSquareOff) {
      onSquareOff(recordedTradeRef.current, liveLTP, 'Greek Tracker Square Off');
    }
  };

  const handleReset = () => {
    setTracking(false);
    setTrade(null);
    setSquaredOff(false);
    setExitLTP(null);
    setStartError(null);
    setEntryPremium('');
    setStrikePrice(recommendedStrikePrice ? String(recommendedStrikePrice) : '');
    recordedTradeRef.current = null;
  };

  const fmt   = v => v != null ? v.toFixed(2) : '—';
  const fmtI  = v => v != null ? Math.round(v) : '—';
  const fmtPct = v => v != null ? v.toFixed(1) + '%' : '—';

  // Guard AFTER all hooks — never call return null before hooks
  if (!gammaData || !gammaData.hasValidData || gammaData.atmCallDelta == null) return null;

  return (
    <div className="gsl-panel">
      <h3 className="gsl-title">
        {tracking ? (squaredOff ? '✅ Trade Closed' : '🔴 Live Trade Tracker') : 'Greek Stop Loss & Trade Tracker'}
      </h3>

      {/* ── ATM Greeks summary ─────────────────────────────────────────────── */}
      <div className="gsl-greeks-grid">
        <div className="gsl-greek-item"><span className="gsl-greek-label">ATM Strike</span><span className="gsl-greek-value">{fmtI(atmStrike)}</span></div>
        <div className="gsl-greek-item"><span className="gsl-greek-label">CE Delta</span><span className="gsl-greek-value gsl-call">{fmt(atmCallDelta)}</span></div>
        <div className="gsl-greek-item"><span className="gsl-greek-label">PE Delta</span><span className="gsl-greek-value gsl-put">{fmt(atmPutDelta)}</span></div>
        <div className="gsl-greek-item"><span className="gsl-greek-label">Gamma</span><span className="gsl-greek-value">{atmGamma != null ? atmGamma.toFixed(5) : '—'}</span></div>
        <div className="gsl-greek-item"><span className="gsl-greek-label">CE Theta/day</span><span className="gsl-greek-value gsl-theta">{fmt(atmCallTheta)}</span></div>
        <div className="gsl-greek-item"><span className="gsl-greek-label">PE Theta/day</span><span className="gsl-greek-value gsl-theta">{fmt(atmPutTheta)}</span></div>
        <div className="gsl-greek-item"><span className="gsl-greek-label">Vega/1%IV</span><span className="gsl-greek-value gsl-vega">{fmt(atmVega)}</span></div>
        <div className="gsl-greek-item"><span className="gsl-greek-label">CE IV</span><span className="gsl-greek-value gsl-call">{fmtPct(atmCallIV)}</span></div>
        <div className="gsl-greek-item"><span className="gsl-greek-label">PE IV</span><span className="gsl-greek-value gsl-put">{fmtPct(atmPutIV)}</span></div>
        <div className="gsl-greek-item"><span className="gsl-greek-label">Lot Size</span><span className="gsl-greek-value">{lotSize}</span></div>
      </div>

      {/* ── Buyer health bar ───────────────────────────────────────────────── */}
      <div className="gsl-buyer-bar">
        <span className={`gsl-zone-badge gsl-zone-${timeZone === 'OPTIMAL' ? 'ok' : 'warn'}`}>
          {timeZone === 'AVOID_OPEN' ? 'AVOID OPEN' : timeZone === 'AVOID_CLOSE' ? 'AVOID CLOSE' : 'OPTIMAL'}
        </span>
        {ivRank != null && (
          <span className="gsl-iv-rank"
            style={{ background: ivRank < 30 ? '#e8f5e9' : ivRank < 50 ? '#e3f2fd' : ivRank < 70 ? '#fff3e0' : '#ffebee',
                     color:      ivRank < 30 ? '#2e7d32' : ivRank < 50 ? '#1565c0' : ivRank < 70 ? '#e65100' : '#c62828' }}
            title={ivPercentile != null ? `IV Percentile: ${ivPercentile.toFixed(0)}%` : ''}>
            IV Rank {ivRank.toFixed(0)}%{ivRank < 30 && ' — Cheap'}{ivRank >= 70 && ' — Expensive'}
          </span>
        )}
        {thetaPct != null && (
          <span className={`gsl-theta-carry ${thetaCarryDanger ? 'gsl-theta-danger' : thetaCarryWarn ? 'gsl-theta-warn' : 'gsl-theta-ok'}`}>
            Carry {thetaPct.toFixed(1)}%/day{thetaCarryDanger && ' — TOO HIGH'}
          </span>
        )}
        {(atmCallLTP != null || atmPutLTP != null) && (
          <span className="gsl-atm-ltp">
            ATM {trSide === 'CE' ? `CE ₹${atmCallLTP != null ? atmCallLTP.toFixed(0) : '—'}` : `PE ₹${atmPutLTP != null ? atmPutLTP.toFixed(0) : '—'}`}
          </span>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          TRACKING MODE — live P&L + SL status
          ════════════════════════════════════════════════════════════════════ */}
      {tracking && trade && (
        <div className="gsl-tracker">

          {/* ── Trade summary bar ──────────────────────────────────────────── */}
          <div className="gsl-tracker-header">
            <span className={`gsl-tracker-side gsl-tracker-side-${trade.side.toLowerCase()}`}>
              {trade.side === 'CE' ? '▲ CALL' : '▼ PUT'}
            </span>
            <span className="gsl-tracker-meta">
              Strike {trade.strikePrice ?? '—'} · Entry ₹{trade.ep} · {lotsNum} lot{lotsNum > 1 ? 's' : ''} · SL {mlp}% · {trade.entryTime}
            </span>
            {!isClosed && (
              <span className="gsl-tracker-live-dot" title="Live tracking active" />
            )}
          </div>

          {/* ── Closed / Squared Off result ────────────────────────────────── */}
          {isClosed && finalExitLTP != null && (
            <div className={`gsl-exit-result ${exitPnlRs != null && exitPnlRs >= 0 ? 'gsl-exit-profit' : 'gsl-exit-loss'}`}>
              <div className="gsl-exit-title">
                {backendClosedRecord && !squaredOff ? 'CLOSED BY BACKEND' : 'SQUARED OFF'} @ ₹{finalExitLTP.toFixed(2)}
              </div>
              {backendClosedRecord?.exitReason && !squaredOff && (
                <div className="gsl-exit-reason">{backendClosedRecord.exitReason}</div>
              )}
              <div className="gsl-exit-pnl">
                {exitPnlPts != null && (exitPnlPts >= 0 ? '+' : '')}{exitPnlPts?.toFixed(2)} pts
                {exitPnlRs != null && <span> · {exitPnlRs >= 0 ? '+' : ''}₹{Math.abs(exitPnlRs).toLocaleString('en-IN')}</span>}
              </div>
              <button className="gsl-btn gsl-btn-reset" onClick={handleReset}>New Trade</button>
            </div>
          )}

          {/* ── Waiting for the backend's own tracker to confirm this position ──── */}
          {!isClosed && !backendPos && (
            <div className="gsl-waiting-backend">
              ⏳ Waiting for the backend to confirm tracking (next poll) — showing this tab's own live price meanwhile.
            </div>
          )}

          {/* ── Live P&L ───────────────────────────────────────────────────── */}
          {!isClosed && liveLTP != null && (
            <>
              <div className="gsl-pnl-row">
                <div className="gsl-pnl-block">
                  <span className="gsl-pnl-label">Live LTP</span>
                  <span className="gsl-pnl-val">₹{liveLTP.toFixed(2)}</span>
                </div>
                <div className="gsl-pnl-block">
                  <span className="gsl-pnl-label">P&amp;L (pts)</span>
                  <span className={`gsl-pnl-val ${pnlPts != null && pnlPts >= 0 ? 'gsl-pnl-profit' : 'gsl-pnl-loss'}`}>
                    {pnlPts != null ? (pnlPts >= 0 ? '+' : '') + pnlPts.toFixed(2) : '—'}
                  </span>
                </div>
                <div className="gsl-pnl-block">
                  <span className="gsl-pnl-label">P&amp;L (₹)</span>
                  <span className={`gsl-pnl-val ${pnlRs != null && pnlRs >= 0 ? 'gsl-pnl-profit' : 'gsl-pnl-loss'}`}>
                    {pnlRs != null ? (pnlRs >= 0 ? '+' : '') + '₹' + Math.abs(pnlRs).toLocaleString('en-IN') : '—'}
                  </span>
                </div>
                <div className="gsl-pnl-block">
                  <span className="gsl-pnl-label">P&amp;L %</span>
                  <span className={`gsl-pnl-val ${pnlPct != null && pnlPct >= 0 ? 'gsl-pnl-profit' : 'gsl-pnl-loss'}`}>
                    {pnlPct != null ? (pnlPct >= 0 ? '+' : '') + pnlPct.toFixed(1) + '%' : '—'}
                  </span>
                </div>
              </div>

              {/* ── SL status bar ───────────────────────────────────────────── */}
              <div className={`gsl-sl-bar ${slBreached ? 'gsl-sl-breached' : ''}`}>
                <div className="gsl-sl-bar-labels">
                  <span className="gsl-sl-bar-label">SL: ₹{activeSL?.toFixed(2) ?? '—'}</span>
                  {backendPos?.peakLTP > trade.ep && <span className="gsl-sl-trailed-tag">TRAILED ↑</span>}
                  <span className="gsl-sl-bar-label gsl-sl-right">Current: ₹{liveLTP?.toFixed(2) ?? '—'}</span>
                </div>
                {activeSL != null && trade.ep > 0 && (
                  <div className="gsl-sl-progress-track">
                    <div
                      className={`gsl-sl-progress-fill ${slBreached ? 'gsl-sl-fill-breach' : 'gsl-sl-fill-safe'}`}
                      style={{ width: `${Math.min(100, Math.max(0, (liveLTP - activeSL) / (trade.ep * 1.5 - activeSL) * 100))}%` }}
                    />
                  </div>
                )}
                {slBreached && (
                  <div className="gsl-sl-breach-alert">
                    ⚠️ STOP LOSS HIT — LTP ₹{liveLTP?.toFixed(2)} ≤ SL ₹{activeSL?.toFixed(2)} — backend is closing this now.
                  </div>
                )}
              </div>

              {/* ── Trail milestone progress — informational, backend-reported. Auto-close
                  and the trailing SL above are what actually protects the position; these
                  milestones are just a heads-up (matches how SYSTEM positions behave). ──── */}
              <div className="gsl-milestones">
                {TRAIL_MILESTONES.map(m => {
                  const reached = milestonesHit.includes(m.id);
                  const gainPct = pnlPct ?? 0;
                  const progress = Math.min(100, Math.max(0, gainPct / m.gainPct * 100));
                  return (
                    <div key={m.id} className={`gsl-ms-row ${reached ? 'gsl-ms-reached' : ''}`}>
                      <span className="gsl-ms-label">{m.label}</span>
                      <div className="gsl-ms-bar-track">
                        <div className="gsl-ms-bar-fill" style={{ width: `${progress}%` }} />
                      </div>
                      <span className="gsl-ms-action">{reached ? '✓ ' : ''}{m.action}</span>
                    </div>
                  );
                })}
              </div>

              {/* ── Action buttons ──────────────────────────────────────────── */}
              <div className="gsl-action-btns">
                <button
                  className={`gsl-btn gsl-btn-squareoff ${slBreached ? 'gsl-btn-squareoff-urgent' : ''}`}
                  onClick={handleSquareOff}
                >
                  {slBreached ? '🚨 Square Off (SL Hit)' : 'Square Off'}
                </button>
                <button className="gsl-btn gsl-btn-reset-sm" onClick={handleReset}>
                  Reset
                </button>
              </div>

              {/* Delta exit warning */}
              {deltaLow && (
                <div className="gsl-delta-exit-warn">
                  Delta {delta.toFixed(2)} near exit threshold (0.20) — option going OTM, consider exiting
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          CALCULATOR MODE — inputs + Track button
          ════════════════════════════════════════════════════════════════════ */}
      {!tracking && (
        <>
          <div className="gsl-inputs">
            <div className="gsl-input-group">
              <label className="gsl-input-label">Side</label>
              <div className="gsl-toggle">
                <button className={`gsl-toggle-btn ${side === 'CE' ? 'gsl-toggle-call' : ''}`} onClick={() => setSide('CE')}>CALL</button>
                <button className={`gsl-toggle-btn ${side === 'PE' ? 'gsl-toggle-put' : ''}`}  onClick={() => setSide('PE')}>PUT</button>
              </div>
            </div>
            <div className="gsl-input-group">
              <label className="gsl-input-label">Strike Price
                {atmStrike && <span className="gsl-input-hint"> (ATM {atmStrike})</span>}
              </label>
              <input className="gsl-input" type="number" min="0"
                placeholder={atmStrike ? `ATM: ${atmStrike}` : 'e.g. 24000'}
                value={strikePrice}
                onChange={e => setStrikePrice(e.target.value)} />
            </div>
            <div className="gsl-input-group">
              <label className="gsl-input-label">Entry Premium (₹)</label>
              <input className="gsl-input" type="number" min="0" placeholder="e.g. 120"
                value={entryPremium} onChange={e => setEntryPremium(e.target.value)} />
            </div>
            <div className="gsl-input-group">
              <label className="gsl-input-label">Lots</label>
              <input className="gsl-input" type="number" min="1" placeholder="1"
                value={lots} onChange={e => setLots(e.target.value)} />
            </div>
            <div className="gsl-input-group">
              <label className="gsl-input-label">Max Loss %</label>
              <input className="gsl-input" type="number" min="1" max="100" placeholder="30"
                value={maxLossPct} onChange={e => setMaxLossPct(e.target.value)} />
            </div>
            <div className="gsl-input-group">
              <label className="gsl-input-label">Capital (₹) <span style={{color:'#aaa',fontWeight:400}}>optional</span></label>
              <input className="gsl-input" type="number" min="0" placeholder="e.g. 500000"
                value={accountCapital} onChange={e => setAccountCapital(e.target.value)} />
            </div>
          </div>

          {/* ── DTE Budget Rule ──────────────────────────────────────────────── */}
          <div className="gsl-dte-section">
            <div className="gsl-dte-section-header">
              <span className="gsl-dte-section-title">DTE Budget Rule</span>
              {daysToExpiry != null && (
                <span className="gsl-dte-section-current">
                  {daysToExpiry === 0 ? '0DTE' : `${daysToExpiry}d`} · max {dteBudgetPct}% capital
                </span>
              )}
            </div>
            <div className="gsl-dte-ladder">
              {[
                { label: '0 DTE',   pct: 0.5, active: daysToExpiry === 0,                        color: '#b71c1c', note: 'Gamma risk max' },
                { label: '1–2 DTE', pct: 1.0, active: daysToExpiry >= 1 && daysToExpiry <= 2,    color: '#c62828', note: 'Need move today' },
                { label: '3–7 DTE', pct: 2.0, active: daysToExpiry >= 3 && daysToExpiry <= 7,    color: '#e65100', note: 'Weekly expiry' },
                { label: '8+ DTE',  pct: 3.0, active: daysToExpiry != null && daysToExpiry >= 8, color: '#2e7d32', note: 'Monthly — safest' },
              ].map(rung => (
                <div key={rung.label} className={`gsl-dte-rung ${rung.active ? 'gsl-dte-rung-active' : ''}`}
                  style={{ borderLeftColor: rung.active ? rung.color : '#e0e0e0' }}>
                  <span className="gsl-dte-rung-label">{rung.label}</span>
                  <span className="gsl-dte-rung-pct" style={{ color: rung.active ? rung.color : '#bbb' }}>{rung.pct}%</span>
                  <span className="gsl-dte-rung-note">{rung.note}</span>
                  {rung.active && dteBudgetRs != null && (
                    <span className="gsl-dte-rung-rs" style={{ color: rung.color }}>₹{Math.round(dteBudgetRs).toLocaleString('en-IN')}</span>
                  )}
                  {rung.active && <span className="gsl-dte-rung-tag" style={{ color: rung.color }}>◄</span>}
                </div>
              ))}
            </div>
            {capitalNum > 0 ? (
              <>
                {ep > 0 && (
                  <div className="gsl-dte-calc-grid">
                    <div className="gsl-dte-calc-row">
                      <span className="gsl-dte-calc-label">Risk / lot</span>
                      <span className="gsl-dte-calc-val">
                        ₹{riskPerLot != null ? Math.round(riskPerLot).toLocaleString('en-IN') : '—'}
                        <span className="gsl-dte-calc-hint"> (₹{ep} × {mlp}% × {lotSize})</span>
                      </span>
                    </div>
                    <div className="gsl-dte-calc-row">
                      <span className="gsl-dte-calc-label">Max lots</span>
                      <span className={`gsl-dte-calc-val ${maxLotsByBudget === 0 ? 'gsl-danger' : maxLotsByBudget != null && maxLotsByBudget >= 1 ? 'gsl-call' : ''}`}>
                        {maxLotsByBudget != null ? maxLotsByBudget === 0 ? "Can't trade — risk/lot exceeds budget" : `${maxLotsByBudget} lot${maxLotsByBudget !== 1 ? 's' : ''}` : '—'}
                      </span>
                    </div>
                  </div>
                )}
                {budgetUtilPct != null && (
                  <div className="gsl-dte-util">
                    <div className="gsl-dte-util-row">
                      <span className="gsl-dte-util-label">Budget used</span>
                      <span className={`gsl-dte-util-pct ${budgetUtilPct > 100 ? 'gsl-danger' : budgetUtilPct > 80 ? 'gsl-theta' : ''}`}>
                        {Math.round(budgetUtilPct)}%
                        <span className="gsl-dte-util-detail">
                          {budgetUtilPct > 100
                            ? ` (over by ₹${Math.round(totalRiskRs - dteBudgetRs).toLocaleString('en-IN')})`
                            : ` (₹${Math.round(totalRiskRs).toLocaleString('en-IN')} of ₹${Math.round(dteBudgetRs).toLocaleString('en-IN')})`}
                        </span>
                      </span>
                    </div>
                    <div className="gsl-dte-bar-track">
                      <div className={`gsl-dte-bar-fill ${budgetBreached ? 'gsl-dte-bar-breach' : budgetUtilPct > 80 ? 'gsl-dte-bar-warn' : 'gsl-dte-bar-ok'}`}
                        style={{ width: `${Math.min(100, budgetUtilPct)}%` }} />
                    </div>
                  </div>
                )}
                {budgetBreached && (
                  <div className="gsl-dte-breach-banner">
                    OVER BUDGET — ₹{Math.round(totalRiskRs).toLocaleString('en-IN')} risk vs ₹{Math.round(dteBudgetRs).toLocaleString('en-IN')} limit.
                    {maxLotsByBudget != null && maxLotsByBudget > 0
                      ? ` Reduce to ${maxLotsByBudget} lot${maxLotsByBudget !== 1 ? 's' : ''} or lower the SL%.`
                      : ' Risk per lot exceeds budget — skip this trade.'}
                  </div>
                )}
              </>
            ) : (
              <div className="gsl-dte-capital-hint">Enter capital above to see ₹ budget limit and max lot calculation.</div>
            )}
          </div>
        </>
      )}

      {/* ── Calculator results (shown in both modes) ───────────────────────── */}
      {ep > 0 && (
        <>
          <div className="gsl-results">
            <div className="gsl-result-item gsl-result-primary">
              <span className="gsl-result-label">Premium SL</span>
              <span className="gsl-result-value">
                ₹{fmt(tracking ? activeSL : premiumSL)}
                {tracking && backendPos?.peakLTP > trade?.ep && <span className="gsl-result-sub"> (trailed ↑)</span>}
                {!tracking && <span className="gsl-result-sub">&nbsp;(−{mlp}% from ₹{ep})</span>}
              </span>
            </div>
            <div className="gsl-result-item gsl-result-primary">
              <span className="gsl-result-label">Spot SL <span className="gsl-result-hint">(Δ-based)</span></span>
              <span className={`gsl-result-value ${trSide === 'CE' ? 'gsl-put' : 'gsl-call'}`}>
                {fmtI(spotSL)}
                {spotMove != null && <span className="gsl-result-sub">&nbsp;({trSide === 'CE' ? '−' : '+'}{fmtI(spotMove)} pts)</span>}
              </span>
            </div>
            <div className="gsl-result-item"><span className="gsl-result-label">Breakeven Spot</span><span className="gsl-result-value gsl-neutral">{fmtI(breakeven)}</span></div>
            <div className="gsl-result-item">
              <span className="gsl-result-label">Theta/day</span>
              <span className="gsl-result-value gsl-theta">−₹{Math.round(thetaPerDay)}</span>
            </div>
            <div className="gsl-result-item">
              <span className="gsl-result-label">Days → Theta SL</span>
              <span className={`gsl-result-value ${daysThetaSL != null && daysThetaSL <= 3 ? 'gsl-danger' : 'gsl-neutral'}`}>
                {daysThetaSL != null
                  ? daysThetaSL < 1 ? `${(daysThetaSL * 24).toFixed(1)}h` : `${daysThetaSL.toFixed(1)}d`
                  : '—'}
              </span>
            </div>
            <div className="gsl-result-item"><span className="gsl-result-label">Vega (5% drop)</span><span className="gsl-result-value gsl-vega">−₹{Math.round(vegaRisk5pct)}</span></div>
            <div className="gsl-result-item">
              <span className="gsl-result-label">Max Loss</span>
              <span className="gsl-result-value gsl-danger">
                ₹{Math.round(maxLossRs).toLocaleString('en-IN')}
                <span className="gsl-result-sub">&nbsp;(₹{ep} × {usedLotSize} × {lotsNum})</span>
              </span>
            </div>
            {tracking && tradeValue != null && (
              <div className="gsl-result-item gsl-result-primary">
                <span className="gsl-result-label">Trade Value</span>
                <span className={`gsl-result-value ${pnlPts != null && pnlPts >= 0 ? 'gsl-call' : 'gsl-danger'}`}>
                  ₹{Math.round(tradeValue).toLocaleString('en-IN')}
                  <span className="gsl-result-sub">&nbsp;({liveLTP?.toFixed(2)} × {usedLotSize} × {lotsNum})</span>
                </span>
              </div>
            )}
          </div>

          <div className={`gsl-verdict gsl-verdict-${riskDriver.toLowerCase()}`}>
            <span className="gsl-verdict-label">Primary Risk</span>
            <span className="gsl-verdict-desc">
              {riskDriver === 'THETA' && `THETA — −₹${Math.round(thetaPerDay)}/day; SL from time alone in ${daysThetaSL != null && daysThetaSL < 1 ? `${(daysThetaSL * 24).toFixed(1)}h` : `${daysThetaSL?.toFixed(1)}d`}`}
              {riskDriver === 'VEGA'  && `VEGA — 5% IV drop costs ₹${Math.round(vegaRisk5pct)} (${((vegaRisk5pct/totalRiskRs)*100).toFixed(0)}% of max loss)`}
              {riskDriver === 'DELTA' && `DELTA — spot must move ${fmtI(spotMove)} pts to hit SL (Δ=${delta.toFixed(2)})`}
            </span>
          </div>

          {!tracking && (
            <div className="gsl-note">
              {trSide === 'CE'
                ? `Exit if spot drops to ${fmtI(spotSL)} OR premium falls to ₹${fmt(premiumSL)} — whichever first.`
                : `Exit if spot rises to ${fmtI(spotSL)} OR premium falls to ₹${fmt(premiumSL)} — whichever first.`}
            </div>
          )}

          {deltaLow && !tracking && (
            <div className="gsl-delta-exit-warn">Delta {delta.toFixed(2)} approaching exit threshold (0.20) — consider exiting</div>
          )}

          {/* ── Profit trail rules ──────────────────────────────────────────── */}
          {!tracking && (
            <div className="gsl-trail-rules">
              <div className="gsl-trail-title">Profit Trailing Rules</div>
              <div className="gsl-trail-grid">
                <div className="gsl-trail-row"><span className="gsl-trail-trigger">At +30% (₹{Math.round(ep*1.3)})</span><span className="gsl-trail-action">Move SL to breakeven</span></div>
                <div className="gsl-trail-row"><span className="gsl-trail-trigger">At +50% (₹{Math.round(ep*1.5)})</span><span className="gsl-trail-action">Trail SL to +20%</span></div>
                <div className="gsl-trail-row"><span className="gsl-trail-trigger">At +100% (₹{Math.round(ep*2.0)})</span><span className="gsl-trail-action">Exit half, trail rest</span></div>
              </div>
            </div>
          )}

        </>
      )}

      {/* ── Track button — outside ep>0 guard so it's always reachable ─────── */}
      {!tracking && parseFloat(entryPremium) > 0 && (
        <>
          <button className="gsl-track-btn" onClick={handleStartTracking} disabled={starting}>
            {starting ? 'Checking with backend…' : '🎯 Track this Trade'}
          </button>
          {startError && <div className="gsl-start-error">⚠️ {startError}</div>}
        </>
      )}
    </div>
  );
};

export default GreekStopLossPanel;
